import { Children, isValidElement, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import type { Components } from "react-markdown";
import AdminPage from "./Admin";
import { MermaidDiagram } from "./MermaidDiagram";
import ResumePage from "./Resume";
import UsPage from "./Us";
import { checkAdminSession, fetchBooks, fetchCollection, fetchCollections, type ApiCollection } from "./data/api";
import type { Book, Issue } from "./data/seed";

function SiteFrame({ children }: { children: React.ReactNode }) {
	return (
		<div className="site-frame">
			<nav className="legacy-nav">
				<div className="legacy-nav-left">
					<NavLink to="/" end>Issues</NavLink>
					<NavLink to="/collections">Collections</NavLink>
					<input id="search" name="search" aria-label="搜索文章" placeholder="Search" />
				</div>
				<Link className="legacy-nav-title" to="/" aria-label="返回首页">CQBLOG JOURNAL</Link>
				<div className="legacy-nav-right"><NavLink className="library-link" to="/books">Library</NavLink><NavLink to="/resume">Resume</NavLink><NavLink to="/us">Us</NavLink></div>
			</nav>
			{children}
		</div>
	);
}

function Home({ books, loading, error, singlePage }: { books: Book[]; loading: boolean; error: string | null; singlePage: boolean }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { bookSlug } = useParams();
	const [searchParams] = useSearchParams();
	const openedBook = bookSlug ? books.find((book) => book.slug === bookSlug) : undefined;
	const [activeTimelineBook, setActiveTimelineBook] = useState(bookSlug ?? books[0]?.slug ?? "");
	const requestedSpread = Number(searchParams.get("spread"));
	const initialSpread = Number.isInteger(requestedSpread) && requestedSpread >= 0 ? requestedSpread : 0;

	useLayoutEffect(() => {
		if (bookSlug || loading || !location.state?.closedBookSlug) return;
		const stage = document.getElementById(`book-${location.state.closedBookSlug}`);
		stage?.scrollIntoView({ block: "start", behavior: "instant" });
		stage?.querySelector<HTMLButtonElement>(".book-cover")?.focus({ preventScroll: true });
	}, [bookSlug, loading, books, location.key, location.state]);

	useEffect(() => {
		if (openedBook) {
			setActiveTimelineBook(openedBook.slug);
			return;
		}
		const stages = [...document.querySelectorAll<HTMLElement>(".book-stage[data-book]")];
		if (!stages.length) return;
		const observer = new IntersectionObserver((entries) => {
			const visible = entries
				.filter((entry) => entry.isIntersecting)
				.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
			const slug = visible?.target.getAttribute("data-book");
			if (slug) setActiveTimelineBook(slug);
		}, { rootMargin: "-28% 0px -48%", threshold: [0, .25, .5, .75, 1] });
		stages.forEach((stage) => observer.observe(stage));
		return () => observer.disconnect();
	}, [books, openedBook?.slug]);

	if (loading) return <main className="data-state"><p>正在打开书架…</p></main>;
	if (error) return <main className="data-state"><p>{error}</p><button type="button" onClick={() => window.location.reload()}>重新加载</button></main>;
	if (bookSlug && !openedBook) return <NotFound />;

	return (
		<main className="home-page">
			<BookTimeline books={books} activeSlug={openedBook?.slug ?? activeTimelineBook} />
			<div className="book-shelf" aria-label="Books">
				{books.map((book, index) => <BookCard key={book.slug} book={book} index={index} bookCount={books.length} singlePage={singlePage} isOpen={book.slug === openedBook?.slug} initialSpread={book.slug === openedBook?.slug ? initialSpread : 0} onOpen={() => navigate(`/books/${book.slug}?spread=0`)} onClose={() => navigate("/", { replace: true, state: { closedBookSlug: book.slug } })} onSpreadChange={(nextSpread) => navigate(`/books/${book.slug}?spread=${nextSpread}`, { replace: true })} />)}
			</div>
		</main>
	);
}

function BookTimeline({ books, activeSlug }: { books: Book[]; activeSlug: string }) {
	const activeIndex = Math.max(0, books.findIndex((book) => book.slug === activeSlug));
	const tickCount = Math.max(25, books.length * 6);
	const activePosition = books.length > 1 ? 8 + (activeIndex / (books.length - 1)) * 84 : 50;
	return <aside className="book-timeline" aria-label="Book timeline" style={{ "--timeline-active": `${activePosition}%` } as CSSProperties}>
		<div className="book-timeline-progress" aria-hidden="true" />
		<div className="book-timeline-ruler">
			{Array.from({ length: tickCount }, (_, index) => <span key={`tick-${index}`} className="book-timeline-tick" style={{ top: `${(index / (tickCount - 1)) * 100}%` }} aria-hidden="true" />)}
			{books.map((book, index) => {
				const position = books.length > 1 ? 8 + (index / (books.length - 1)) * 84 : 50;
				return <Link key={book.slug} className={`book-timeline-book${book.slug === activeSlug ? " is-active" : ""}`} style={{ "--timeline-position": `${position}%` } as CSSProperties} to={`/books/${book.slug}?spread=0`} aria-current={book.slug === activeSlug ? "page" : undefined} aria-label={`${book.createdAt} ${book.subtitle}`}><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><small>{book.subtitle}</small></Link>;
			})}
		</div>
	</aside>;
}

// Three entries per physical page; a partial spread shares its entries between both pages.
const ISSUES_PER_PAGE = 3;
const MOBILE_ISSUES_PER_PAGE = 2;
const SINGLE_PAGE_MEDIA_QUERY = "(max-width: 720px), (max-width: 900px) and (max-height: 500px)";
const COLLECTION_COLORS = ["#b4ffc8", "#ded6ff", "#f4c6a6", "#d8d1ff", "#ffd19f"];

const MARKDOWN_COMPONENTS: Components = {
	code({ className, children }) {
		if (className?.split(/\s+/).includes("language-mermaid")) {
			return <MermaidDiagram chart={String(children).replace(/\n$/, "")} />;
		}
		return <code className={className}>{children}</code>;
	},
	pre({ children }) {
		const childNodes = Children.toArray(children);
		const child = childNodes.length === 1 ? childNodes[0] : undefined;
		if (isValidElement<{ className?: string; children?: ReactNode }>(child) && child.props.className?.split(/\s+/).includes("language-mermaid")) {
			return <MermaidDiagram chart={String(child.props.children).replace(/\n$/, "")} />;
		}
		return <pre>{children}</pre>;
	},
};

function BookCard({ book, index, bookCount, singlePage, isOpen, initialSpread, onOpen, onClose, onSpreadChange }: { book: Book; index: number; bookCount: number; singlePage: boolean; isOpen: boolean; initialSpread: number; onOpen: () => void; onClose: () => void; onSpreadChange: (spread: number) => void }) {
	const stageRef = useRef<HTMLElement>(null);
	const sceneRef = useRef<HTMLDivElement>(null);
	const coverRef = useRef<HTMLButtonElement>(null);
	const wasOpen = useRef(isOpen);
	const [instant, setInstant] = useState(false);
	const [turn, setTurn] = useState<{ from: number; to: number; direction: "next" | "previous" } | null>(null);
	const spreadCount = Math.max(1, Math.ceil(book.issues.length / (singlePage ? MOBILE_ISSUES_PER_PAGE : ISSUES_PER_PAGE * 2)));
	const safeInitialSpread = Math.min(initialSpread, spreadCount - 1);
	const [spread, setSpread] = useState(safeInitialSpread);

	useEffect(() => {
		setSpread(safeInitialSpread);
		setTurn(null);
		if (isOpen) {
			stageRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
			stageRef.current?.focus({ preventScroll: true });
		} else if (wasOpen.current) {
			setTurn(null);
			coverRef.current?.focus({ preventScroll: true });
		}
		wasOpen.current = isOpen;
	}, [isOpen, safeInitialSpread]);

	function turnPage(direction: "next" | "previous", fromKeyboard: boolean) {
		if (turn) return;
		const next = spread + (direction === "next" ? 1 : -1);
		if (next < 0 || next >= spreadCount) return;
		if (fromKeyboard) {
			setSpread(next);
			onSpreadChange(next);
		}
		else setTurn({ from: spread, to: next, direction });
	}

	function closeWhenClickingOutside(event: MouseEvent<HTMLElement>) {
		if (isOpen && sceneRef.current && !sceneRef.current.contains(event.target as Node)) {
			setInstant(false);
			onClose();
		}
	}

	const leftSpread = turn?.direction === "previous" ? turn.to : spread;
	const rightSpread = turn?.direction === "next" ? turn.to : spread;

	return (
			<section id={`book-${book.slug}`} ref={stageRef} tabIndex={-1} className={`book-stage${isOpen ? " is-open" : ""}`} data-motion={instant ? "instant" : "animated"} data-book={book.slug} style={{ "--book-color": book.color } as CSSProperties} aria-label={book.subtitle} onClick={closeWhenClickingOutside} onKeyDown={(event) => {
			if (event.key === "Escape" && isOpen) { setInstant(true); onClose(); }
			if (isOpen && event.key === "ArrowLeft") turnPage("previous", true);
			if (isOpen && event.key === "ArrowRight") turnPage("next", true);
		}}>
			<div ref={sceneRef} className="book-scene">
				<div className="physical-book">
					<div className="book-board" aria-hidden="true" />
					<div className="book-right-paper" id={singlePage ? `contents-${book.slug}` : undefined} inert={!isOpen}>
						<BookOpenView book={book} side="right" singlePage={singlePage} spread={rightSpread} onPageClick={() => turnPage("next", false)} />
					</div>
					<div className="book-cover-leaf">
						<button ref={coverRef} className="book-cover book-face" type="button" inert={isOpen} aria-expanded={isOpen} aria-controls={`contents-${book.slug}`} onClick={(event) => { setInstant(event.detail === 0); onOpen(); }} aria-label={`打开 ${book.subtitle}`}>
							<img src={book.cover} alt="" />
							<span className="book-cover-wash" />
							<span className="book-cover-title"><small>CQBLOG / VOLUME {String(index + 1).padStart(2, "0")}</small><strong>{book.title}</strong><em>{book.subtitle}</em></span>
							<span className="book-cover-issue">{String(book.issues.length).padStart(2, "0")}</span>
						</button>
						{!singlePage && <div className="book-face book-inside-cover" id={`contents-${book.slug}`} inert={!isOpen}>
							<BookOpenView book={book} side="left" singlePage={singlePage} spread={leftSpread} onPageClick={() => turnPage("previous", false)} />
						</div>}
					</div>
					{turn && <div className={`book-turning-leaf turn-${turn.direction}`} inert aria-hidden="true" onAnimationEnd={(event) => {
						if (event.target !== event.currentTarget) return;
						setSpread(turn.to);
						onSpreadChange(turn.to);
						setTurn(null);
					}}>
						<div className="book-face"><BookOpenView book={book} side="right" singlePage={singlePage} spread={turn.direction === "next" ? turn.from : turn.to} /></div>
						{!singlePage && <div className="book-face book-turn-back"><BookOpenView book={book} side="left" singlePage={singlePage} spread={turn.direction === "next" ? turn.to : turn.from} /></div>}
					</div>}
				</div>
			</div>
			{singlePage && isOpen && <div className="book-mobile-controls" onClick={(event) => event.stopPropagation()}>
				<button className="book-mobile-back" type="button" onClick={onClose}>← 书架</button>
				<div className="book-mobile-pagination">
					<button type="button" aria-label="目录上一页" disabled={spread === 0 || !!turn} onClick={() => turnPage("previous", false)}>←</button>
					<span aria-live="polite">{String(spread + 1).padStart(2, "0")} / {String(spreadCount).padStart(2, "0")}</span>
					<button type="button" aria-label="目录下一页" disabled={spread === spreadCount - 1 || !!turn} onClick={() => turnPage("next", false)}>→</button>
				</div>
			</div>}
			<div className="book-stage-meta"><span>{String(index + 1).padStart(2, "0")} / {String(bookCount).padStart(2, "0")}</span><time dateTime={book.createdAt}>{book.createdAt}</time></div>
			<div className="book-stage-caption"><h2>{book.subtitle}</h2><p>{book.description}</p></div>
		</section>
	);
}

function BookOpenView({ book, side, singlePage, spread, onPageClick }: { book: Book; side: "left" | "right"; singlePage: boolean; spread: number; onPageClick?: () => void }) {
	const entriesPerSpread = singlePage ? MOBILE_ISSUES_PER_PAGE : ISSUES_PER_PAGE * 2;
	const start = spread * entriesPerSpread;
	const entries = book.issues.slice(start, start + entriesPerSpread);
	const splitAt = Math.ceil(entries.length / 2);
	const offset = singlePage || side === "left" ? 0 : splitAt;
	const pageIssues = singlePage ? entries : side === "left" ? entries.slice(0, splitAt) : entries.slice(splitAt);
	const pageNumber = singlePage ? spread + 1 : spread * 2 + (side === "left" ? 1 : 2);

	function handlePageClick(event: MouseEvent<HTMLElement>) {
		if (!onPageClick) return;
		if (event.target instanceof Element && event.target.closest("a, button")) return;
		onPageClick();
	}

	return (
		<section className={`book-page book-page-${side}`} data-clickable={onPageClick ? "true" : undefined} aria-label={pageNumber === 1 ? "目录" : `第 ${pageNumber} 页`} onClick={handlePageClick}>
			<header className="book-running-head"><span>{book.title}</span><span>{book.createdAt.slice(0, 4)}</span></header>
			{(pageNumber === 1 || singlePage) && <div className="book-page-heading"><small>{`${String(book.issues.length).padStart(2, "0")} ISSUES`}</small><h2>目录</h2></div>}
			<ol className="issue-list" start={start + offset + 1}>{pageIssues.map((issue, index) => <IssueRow key={issue.slug} issue={issue} index={start + offset + index} />)}</ol>
			{!pageIssues.length && <p className="book-page-empty">{book.issues.length ? "—" : "暂无文章"}</p>}
			<footer className="book-page-footer"><span>{book.subtitle}</span><span>{String(pageNumber).padStart(2, "0")}</span></footer>
		</section>
	);
}

function IssueRow({ issue, index }: { issue: Issue; index: number }) {
	return <li><Link className="issue-entry" to={`/issues/${issue.slug}`}><span className="issue-index">{String(index + 1).padStart(2, "0")}</span><img src={issue.image} alt="" /><div><time dateTime={issue.date}>{issue.date}</time><h3>{issue.title}</h3><p>{issue.description}</p></div></Link></li>;
}

function IssuePage({ books, loading, error, singlePage }: { books: Book[]; loading: boolean; error: string | null; singlePage: boolean }) {
	const { issueSlug } = useParams();
	const result = issueSlug ? books.reduce<{ book: Book; issue: Issue } | undefined>((found, book) => found ?? (() => {
		const issue = book.issues.find((item) => item.slug === issueSlug);
		return issue ? { book, issue } : undefined;
	})(), undefined) : undefined;
	if (loading) return <main className="data-state"><p>正在打开文章…</p></main>;
	if (error) return <main className="data-state"><p>{error}</p></main>;
	if (!result) return <NotFound />;
	return <IssueReader book={result.book} issue={result.issue} singlePage={singlePage} />;
}

function IssueReader({ book, issue, singlePage }: { book: Book; issue: Issue; singlePage: boolean }) {
	const issueIndex = book.issues.findIndex((item) => item.slug === issue.slug);
	const returnSpread = Math.max(0, Math.floor(issueIndex / (singlePage ? MOBILE_ISSUES_PER_PAGE : ISSUES_PER_PAGE * 2)));
	const returnToBook = `/books/${book.slug}?spread=${returnSpread}`;
	const navigate = useNavigate();
	const markdown = issue.body.join("\n\n");
	const measureBodyRef = useRef<HTMLDivElement>(null);
	const readerRef = useRef<HTMLElement>(null);
	const sceneRef = useRef<HTMLDivElement>(null);
	const [pageCount, setPageCount] = useState(1);
	const [spread, setSpread] = useState(0);
	const [turn, setTurn] = useState<{ from: number; to: number; direction: "next" | "previous" } | null>(null);
	const spreadCount = singlePage ? pageCount + 1 : Math.max(1, Math.ceil((pageCount + 1) / 2));

	useLayoutEffect(() => {
		const body = measureBodyRef.current;
		if (!body) return;
		const measureBody = body;

		function paginate() {
			const availableHeight = measureBody.clientHeight;
			if (!availableHeight) return;
			const content = measureBody.querySelector<HTMLElement>(".issue-book-measure-columns");
			if (!content) return;
			const columnGap = Number.parseFloat(getComputedStyle(content).columnGap) || 0;
			const nextPageCount = Math.max(1, Math.round((content.scrollWidth + columnGap) / (content.clientWidth + columnGap)));
			const nextSpreadCount = singlePage ? nextPageCount + 1 : Math.max(1, Math.ceil((nextPageCount + 1) / 2));
			setPageCount((previous) => previous === nextPageCount ? previous : nextPageCount);
			setSpread((previous) => Math.min(previous, nextSpreadCount - 1));
		}

		paginate();
		const observer = new ResizeObserver(paginate);
		observer.observe(measureBody);
		const mutations = new MutationObserver(paginate);
		mutations.observe(measureBody, { childList: true, subtree: true });
		let active = true;
		document.fonts.ready.then(() => { if (active) paginate(); });
		const images = Array.from(measureBody.querySelectorAll("img"));
		images.forEach((image) => image.addEventListener("load", paginate));
		return () => {
			active = false;
			observer.disconnect();
			mutations.disconnect();
			images.forEach((image) => image.removeEventListener("load", paginate));
		};
	}, [markdown, singlePage]);

	useEffect(() => {
		setSpread(0);
		setTurn(null);
		readerRef.current?.focus({ preventScroll: true });
	}, [issue.slug, singlePage]);

	function turnPage(direction: "next" | "previous") {
		if (turn) return;
		const next = spread + (direction === "next" ? 1 : -1);
		if (next < 0 || next >= spreadCount) return;
		setTurn({ from: spread, to: next, direction });
	}

	function returnWhenClickingOutside(event: MouseEvent<HTMLElement>) {
		if (sceneRef.current && !sceneRef.current.contains(event.target as Node)) navigate(returnToBook);
	}

	const leftSpread = turn?.direction === "previous" ? turn.to : spread;
	const rightSpread = turn?.direction === "next" ? turn.to : spread;
	const pageIndexFor = (spreadIndex: number, side: "left" | "right") => {
		if (singlePage) return spreadIndex - 1;
		if (side === "left") return spreadIndex === 0 ? -1 : spreadIndex * 2 - 1;
		return spreadIndex * 2;
	};
	const renderPage = (pageIndex: number, side: "left" | "right", onPageClick?: () => void) => <IssueBookPage book={book} issue={issue} issueIndex={issueIndex} pageIndex={pageIndex} pageCount={pageCount} markdown={markdown} side={side} singlePage={singlePage} returnToBook={returnToBook} onPageClick={onPageClick} />;

	return <main ref={readerRef} tabIndex={-1} className="issue-reader-page" onClick={returnWhenClickingOutside} onKeyDown={(event) => {
		if (event.key === "Escape") navigate(returnToBook);
		if (event.key === "ArrowLeft") turnPage("previous");
		if (event.key === "ArrowRight") turnPage("next");
	}}>
		<div ref={sceneRef} className="book-scene issue-reader-scene">
			<div className="physical-book issue-reader-book" style={{ "--book-color": book.color } as CSSProperties}>
				<div className="book-board" aria-hidden="true" />
				<div className="book-right-paper"><div className="book-face">{renderPage(pageIndexFor(rightSpread, "right"), "right", rightSpread < spreadCount - 1 ? () => turnPage("next") : undefined)}</div></div>
				{!singlePage && <div className="book-cover-leaf"><div className="book-face book-inside-cover">{renderPage(pageIndexFor(leftSpread, "left"), "left", leftSpread > 0 ? () => turnPage("previous") : undefined)}</div></div>}
				{turn && <div className={`book-turning-leaf turn-${turn.direction}`} inert aria-hidden="true" onAnimationEnd={(event) => {
					if (event.target !== event.currentTarget) return;
					setSpread(turn.to);
					setTurn(null);
				}}>
					<div className="book-face">{renderPage(pageIndexFor(turn.direction === "next" ? turn.from : turn.to, "right"), "right")}</div>
					{!singlePage && <div className="book-face book-turn-back">{renderPage(pageIndexFor(turn.direction === "next" ? turn.to : turn.from, "left"), "left")}</div>}
				</div>}
				<div className="issue-reader-measure" aria-hidden="true">
					<section className="book-page book-page-right issue-book-page">
						<header className="book-running-head"><span>{book.title}</span><span>{book.createdAt.slice(0, 4)}</span></header>
						<div ref={measureBodyRef} className="issue-book-body issue-book-measure-body"><div className="issue-book-columns issue-book-measure-columns" style={{ "--reader-page-count": 1 } as CSSProperties}><ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{markdown}</ReactMarkdown></div></div>
						<footer className="book-page-footer"><span>{book.subtitle}</span><span>01</span></footer>
					</section>
				</div>
			</div>
		</div>
		{singlePage && <div className="book-mobile-controls" onClick={(event) => event.stopPropagation()}>
			<Link className="book-mobile-back" to={returnToBook}>← 目录</Link>
			<div className="book-mobile-pagination">
				<button type="button" aria-label="文章上一页" disabled={spread === 0 || !!turn} onClick={() => turnPage("previous")}>←</button>
				<span aria-live="polite">{String(spread + 1).padStart(2, "0")} / {String(spreadCount).padStart(2, "0")}</span>
				<button type="button" aria-label="文章下一页" disabled={spread === spreadCount - 1 || !!turn} onClick={() => turnPage("next")}>→</button>
			</div>
		</div>}
	</main>;
}

function IssueBookPage({ book, issue, issueIndex, pageIndex, pageCount, markdown, side, singlePage, returnToBook, onPageClick }: { book: Book; issue: Issue; issueIndex: number; pageIndex: number; pageCount: number; markdown: string; side: "left" | "right"; singlePage: boolean; returnToBook: string; onPageClick?: () => void }) {
	const isTitlePage = pageIndex < 0;
	function handlePageClick(event: MouseEvent<HTMLElement>) {
		if (!onPageClick) return;
		if (event.target instanceof Element && event.target.closest("a, button")) return;
		onPageClick();
	}
	return <section className={`book-page book-page-${side} issue-book-page`} data-clickable={onPageClick ? "true" : undefined} aria-label={isTitlePage ? `${issue.title} 标题页` : `${issue.title} 正文第 ${pageIndex + 1} 页`} onClick={handlePageClick}>
		<header className="book-running-head">{isTitlePage ? <Link className="issue-book-back" to={returnToBook}>← {book.subtitle}</Link> : <span>{book.title}</span>}<span>{isTitlePage ? `${singlePage ? "" : "ISSUE / "}${issue.date}` : book.createdAt.slice(0, 4)}</span></header>
		{isTitlePage ? <div className="issue-book-title"><small>{`${String(issueIndex + 1).padStart(2, "0")} ISSUE`}</small><h1>{issue.title}</h1><img src={issue.image} alt="" /></div> : <div className="issue-book-body"><div className="issue-book-columns" style={{ "--reader-page": pageIndex, "--reader-page-count": pageCount } as CSSProperties}><ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>{markdown}</ReactMarkdown></div></div>}
		<footer className="book-page-footer"><span>{book.subtitle}</span>{isTitlePage ? <span>{String(singlePage ? 1 : issueIndex + 1).padStart(2, "0")}</span> : <span className="issue-book-footer-page"><Link to={returnToBook}>目录 ↗</Link><span>{String(pageIndex + 2).padStart(2, "0")}</span></span>}</footer>
	</section>;
}

function Collections() {
	const [items, setItems] = useState<ApiCollection[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		fetchCollections().then(setItems).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "无法读取画册数据")).finally(() => setLoading(false));
	}, []);
	if (loading) return <main className="data-state"><p>正在打开画册…</p></main>;
	if (error) return <main className="data-state"><p>{error}</p></main>;
	const photoCount = items.reduce((total, collection) => total + collection.photos.length, 0);
	return <main className="collections-page">
		<header className="collections-hero">
			<div>
				<p className="collections-kicker">03 / COLLECTIONS</p>
				<h1>画册</h1>
			</div>
			<div className="collections-intro">
				<p>把零散的片段装订成册。每一本画册，记录一个阶段、一个主题，或一段还没有说完的生活。</p>
				<div className="collections-ledger"><span>{String(items.length).padStart(2, "0")} ALBUMS</span><span>{String(photoCount).padStart(2, "0")} PHOTOGRAPHS</span></div>
			</div>
		</header>
		{items.length === 0 ? <div className="collections-empty"><p className="collections-kicker">NO COLLECTIONS YET</p><p>还没有画册。</p><span>从管理后台创建第一本画册。</span></div> : <section className="collections-grid" aria-label="画册列表">
			{items.map((collection, index) => <article key={collection.slug} className={`collection-card${index === 0 ? " collection-card-feature" : ""}`}>
				<Link className="collection-card-cover" to={`/collections/${collection.slug}`} style={{ "--collection-color": COLLECTION_COLORS[index % COLLECTION_COLORS.length] } as CSSProperties} aria-label={`打开画册 ${collection.title}`}>
					{collection.photos[0] && <img src={collection.photos[0].image} alt="" loading={index === 0 ? "eager" : "lazy"} decoding="async" />}
					<span className="collection-card-wash" />
					<span className="collection-card-label">CQBLOG / ALBUM {String(index + 1).padStart(2, "0")}</span>
					<strong>{collection.title}</strong>
					<em>PHOTO COLLECTION</em>
					<span className="collection-card-count">{String(collection.photos.length).padStart(2, "0")} PHOTOS</span>
				</Link>
				<div className="collection-card-details">
					<div className="collection-card-meta"><span>{String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span><time dateTime={collection.createdAt}>{collection.createdAt}</time></div>
					<h2><Link to={`/collections/${collection.slug}`}>{collection.title}</Link></h2>
					<p>{collection.description}</p>
					<div className="collection-card-photos">
						{collection.photos.slice(0, 3).map((photo, photoIndex) => <Link className="collection-card-photo" key={photo.id} to={`/collections/${collection.slug}#photo-${photo.id}`}>
							<span>{String(photoIndex + 1).padStart(2, "0")}</span><img src={photo.image} alt="" loading="lazy" decoding="async" /><span className="collection-card-photo-title">{photo.caption}</span><span aria-hidden="true">↗</span>
						</Link>)}
					</div>
				</div>
			</article>)}
		</section>}
		<footer className="collections-footer"><Link to="/">← 返回书架</Link><span>KEEPING A RECORD OF THE ORDINARY</span></footer>
	</main>;
}

function CollectionPage() {
	const { collectionSlug } = useParams();
	const [collection, setCollection] = useState<ApiCollection | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		if (!collectionSlug) return;
		setLoading(true);
		fetchCollection(collectionSlug).then(setCollection).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "无法读取画册数据")).finally(() => setLoading(false));
	}, [collectionSlug]);
	if (loading) return <main className="data-state"><p>正在打开画册…</p></main>;
	if (error || !collection) return <NotFound />;
	return <main className="collection-detail-page">
		<header className="collection-detail-hero">
			<Link className="collection-detail-back" to="/collections">← 画册</Link>
			<div className="collection-detail-heading">
				<p className="collections-kicker">COLLECTION / {collection.createdAt}</p>
				<h1>{collection.title}</h1>
			</div>
			<div className="collection-detail-intro"><p>{collection.description}</p><span>{String(collection.photos.length).padStart(2, "0")} PHOTOGRAPHS</span></div>
		</header>
		<section className="collection-detail-grid" aria-label={`${collection.title}照片`}>
			{collection.photos.map((photo, index) => <figure id={`photo-${photo.id}`} className={`collection-detail-photo${index === 0 ? " collection-detail-photo-feature" : ""}`} key={photo.id}>
				<img src={photo.image} alt={photo.caption} loading={index === 0 ? "eager" : "lazy"} decoding="async" />
				<figcaption><div><span>{String(index + 1).padStart(2, "0")} / {String(collection.photos.length).padStart(2, "0")}</span></div><p>{photo.caption}</p></figcaption>
			</figure>)}
		</section>
		<footer className="collections-footer"><Link to="/collections">← 返回画册</Link><span>{collection.title}</span></footer>
	</main>;
}
function About() { return <main className="placeholder-page"><p>PERSON</p><h1>关于 CQ。</h1><Link to="/">返回书架</Link></main>; }
function NotFound() { return <main className="placeholder-page"><p>404</p><h1>页面不存在。</h1><Link to="/">回到书架</Link></main>; }

export default function App() {
	const [books, setBooks] = useState<Book[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchBooks().then(setBooks).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "无法读取文章数据")).finally(() => setLoading(false));
	}, []);

	return <Routes>
		<Route path="/admin/*" element={<AdminPage />} />
		<Route path="*" element={<PublicSite books={books} loading={loading} error={error} />} />
	</Routes>;
}

function PublicSite({ books, loading, error }: { books: Book[]; loading: boolean; error: string | null }) {
	const [singlePage, setSinglePage] = useState(() => window.matchMedia(SINGLE_PAGE_MEDIA_QUERY).matches);

	useEffect(() => {
		const media = window.matchMedia(SINGLE_PAGE_MEDIA_QUERY);
		const update = () => setSinglePage(media.matches);
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	return <SiteFrame><Routes>
		<Route path="/" element={<Home books={books} loading={loading} error={error} singlePage={singlePage} />} />
		<Route path="/books" element={<Home books={books} loading={loading} error={error} singlePage={singlePage} />} />
		<Route path="/books/:bookSlug" element={<Home books={books} loading={loading} error={error} singlePage={singlePage} />} />
		<Route path="/issues/:issueSlug" element={<IssuePage books={books} loading={loading} error={error} singlePage={singlePage} />} />
		<Route path="/us" element={<AdminOnlyUsPage />} />
		<Route path="/resume" element={<ResumePage />} />
		<Route path="/collections" element={<Collections />} />
		<Route path="/collections/:collectionSlug" element={<CollectionPage />} />
		<Route path="/about" element={<About />} /><Route path="*" element={<NotFound />} />
	</Routes></SiteFrame>;
}

function AdminOnlyUsPage() {
	const location = useLocation();
	const [authenticated, setAuthenticated] = useState<boolean | null>(null);

	useEffect(() => {
		checkAdminSession()
			.then(setAuthenticated)
			.catch(() => setAuthenticated(false));
	}, []);

	if (authenticated === null) return <main className="data-state"><p>正在验证管理员身份…</p></main>;
	if (!authenticated) {
		const returnTo = `${location.pathname}${location.search}${location.hash}`;
		return <Navigate to="/admin" replace state={{ from: returnTo }} />;
	}
	return <UsPage />;
}
